import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { UseApi } from '../Hooks/UseApi';

// FIXED: StandardImage component that handles presigned URL responses from API
const StandardImage = ({ imageId, imageType = 'consent_form', alt = 'Image', sx = {} }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Failed to load image');
  const [imageSrc, setImageSrc] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Get the correct API URL for an image based on its type
  const getImageApiUrl = () => {
    const timestamp = Date.now();
    const cacheBuster = `?t=${timestamp}&retry=${retryCount}`;
    
    if (imageType === 'consent_form') {
      return `/consent-form-images/${imageId}/secure/${cacheBuster}`;
    } else if (imageType === 'custom_form') {
      return `/custom-form-images/${imageId}/${cacheBuster}`;
    } else if (imageType === 'client_id') {
      return `/clients/${imageId}/secure-id/${cacheBuster}`;
    } else if (imageType === 'client_screening') {
      return `/images/${imageId}/${cacheBuster}&type=client_screening`;
    }
    
    return `/images/${imageId}/${cacheBuster}&type=${imageType}`;
  };

  useEffect(() => {
    if (!imageId) return;
    
    const loadImage = async () => {
      setLoading(true);
      setError(false);
      try {
        // Get auth token
        const token = await getAccessTokenSilently({
          audience: "https://inkedupmanager.com/api",
        });
        
        const apiUrl = getImageApiUrl();
        const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${apiUrl}`;
        // Fetch the presigned URL from our API
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        
        // Check content type to determine response format
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // API returns JSON with presigned URL
          const data = await response.json();
          // Extract the presigned URL from the response
          const presignedUrl = data.url || data.presigned_url || data.image_url;
          
          if (presignedUrl) {
            setImageSrc(presignedUrl);
            setLoading(false);
          } else {
            throw new Error('No URL found in response');
          }
        } else {
          // API returns binary image data directly
          const blob = await response.blob();
          const imageObjectUrl = URL.createObjectURL(blob);
          setImageSrc(imageObjectUrl);
          setLoading(false);
        }
        
      } catch (err) {
        console.error(`Image load failed:`, err);
        setError(true);
        setErrorMessage(err.message);
        setLoading(false);
      }
    };
    
    loadImage();
    
    // Cleanup function - only revoke blob URLs, not presigned URLs
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageId, imageType, retryCount, getAccessTokenSilently]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      minHeight: '300px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      ...sx 
    }}>
      {loading && (
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(128, 128, 128, 0.3)',
          zIndex: 1
        }}>
          <CircularProgress size={40} />
        </Box>
      )}
      
      {error ? (
        <Box sx={{
          width: '100%',
          height: '100%',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 0, 0, 0.05)',
          borderRadius: 1,
          padding: 1
        }}>
          <Typography color="error" variant="body2" align="center" gutterBottom>
            {errorMessage}
          </Typography>
          <Typography variant="caption" color="textSecondary" align="center" gutterBottom>
            Image ID: {imageId} | Type: {imageType}
          </Typography>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleRetry}
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Box>
      ) : (
        imageSrc && (
          <Box sx={{
            maxWidth: '100%',
            maxHeight: '100%', 
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <img
              src={imageSrc}
              alt={alt}
              style={{
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain',
                display: loading ? 'none' : 'block'
              }}
              onError={() => {
                setError(true);
                setErrorMessage('Image failed to display');
              }}
            />
          </Box>
        )
      )}
    </Box>
  );
};

const ConsentFormsPage = () => {
  // Use UseApi hook instead of direct axios calls
  const api = UseApi();
  const apiRef = useRef(api);
  
  const { id } = useParams();
  const navigate = useNavigate();

  // Date filter states
  const [filterOption, setFilterOption] = useState('all');
  const [startDate, setStartDate] = useState(''); // String format 'YYYY-MM-DD'
  const [endDate, setEndDate] = useState('');     // String format 'YYYY-MM-DD'
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filterError, setFilterError] = useState(null);
  
  // Forms and client data states
  const [consentForms, setConsentForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientName, setClientName] = useState('');

  const fetchClientName = useCallback(async () => {
    try {
      const client = await apiRef.current.fetchClient(id);
      setClientName(`${client.first_name} ${client.last_name}`);
    } catch (error) {
      console.error("Error fetching client name:", error);
      setClientName('Unknown Client');
    }
  }, [id]);

  // FIXED: Updated fetchConsentForms function with proper response handling
  const fetchConsentForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFilterError(null);
    try {
      // Build the URL with query parameters for date filtering if not "all"
      let url = `/clients/${id}/consent-forms/`;
      
      if (filterOption === 'date_range' && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      }
      const response = await apiRef.current.fetchWithAuth(url);
      // CRITICAL FIX: Handle the correct response structure
      let formsArray = [];
      
      if (response && response.consent_forms && Array.isArray(response.consent_forms)) {
        // New API structure: {consent_forms: [...], total: X, breakdown: {...}}
        formsArray = response.consent_forms;
        // Log details about each form
        formsArray.forEach((form, index) => {
          console.log(`Form ${index + 1}:`, {
            id: form.id,
            type: form.form_type,
            title: form.template_title || 'N/A',
            date: form.date_signed,
            images: form.images?.length || 0
          });
        });
        
      } else if (Array.isArray(response)) {
        // Fallback: Direct array response
        formsArray = response;
      } else {
        // Fallback: Empty array
        console.warn('Unexpected response structure, setting empty array');
        console.warn('Response was:', response);
        formsArray = [];
      }
      
      setConsentForms(formsArray);
    } catch (error) {
      console.error("Error fetching consent forms:", error);
      
      if (error.message?.includes('401')) {
        setError('Authentication error: Please log in again.');
      } else {
        setError(`Error fetching consent forms: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [id, filterOption, startDate, endDate]);

  // Only fetch client name on load, not forms yet
  useEffect(() => {
    if (id) {
      fetchClientName();
    } else {
      setError('Invalid client ID.');
    }
  }, [id, fetchClientName]);

  const handleBackToProfile = () => {
    navigate(`/client/${id}`);
  };
  
  // Filter handling functions
  const handleFilterOptionChange = (event) => {
    setFilterOption(event.target.value);
    // Reset dates if switching to 'all'
    if (event.target.value === 'all') {
      setStartDate('');
      setEndDate('');
    }
  };
  
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };
  
  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };
  
  const handleApplyFilters = () => {
    setFilterError(null);
    
    // Validation for date range
    if (filterOption === 'date_range') {
      if (!startDate || !endDate) {
        setFilterError('Please select both start and end dates.');
        return;
      }
      
      // Compare dates as strings (YYYY-MM-DD format works for string comparison)
      if (startDate > endDate) {
        setFilterError('Start date must be before end date.');
        return;
      }
    }
    setFiltersApplied(true);
    fetchConsentForms();
  };
  
  const handleClearFilters = () => {
    setFilterOption('all');
    setStartDate('');
    setEndDate('');
    setFilterError(null);
    setFiltersApplied(false);
    setConsentForms([]);
  };

  // Renders a custom form's field responses using the template's element definitions
  // so that field labels show the actual question text instead of the raw key name.
  const renderCustomFormContent = (form) => {
    const formData = form.form_data || {};
    const templateElements = form.template_config?.elements || [];

    // Build a lookup map: field key -> element definition from the template.
    // Element names are set at form-build time and match the keys stored in form_data.
    const elementByKey = {};
    templateElements.forEach((el, idx) => {
      const key = el.name || `field_${idx}`;
      elementByKey[key] = el;
    });

    // Returns the human-readable label for a field key.
    // Priority: element label > question text > content > title > raw key.
    const getLabelForKey = (fieldKey) => {
      const el = elementByKey[fieldKey];
      if (el) {
        return el.label || el.question || el.content || el.title || fieldKey;
      }
      // Fallback: make raw key slightly readable
      return fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
    };

    // Returns a clean display value for any field value type.
    const getDisplayValue = (value) => {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'string') {
        if (value.trim() === '') return '(No response)';
        if (value.length > 300) return `${value.substring(0, 300)}… (${value.length} chars total)`;
        return value;
      }
      if (Array.isArray(value)) return value.join(', ');
      return String(value);
    };

    // Filter internal/system keys and sort by original template element order.
    const fieldEntries = Object.entries(formData)
      .filter(([key]) => !key.startsWith('_') && key !== 'signature')
      .sort(([keyA], [keyB]) => {
        const idxA = templateElements.findIndex((el, i) => (el.name || `field_${i}`) === keyA);
        const idxB = templateElements.findIndex((el, i) => (el.name || `field_${i}`) === keyB);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        return 0;
      });

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          Form Response
          <Chip 
            label={`${fieldEntries.length} field${fieldEntries.length !== 1 ? 's' : ''}`} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        </Typography>
        
        {fieldEntries.length === 0 ? (
          <Alert severity="info">
            <Typography variant="body2">
              No form data found. This may be an empty submission.
            </Typography>
          </Alert>
        ) : (
          <Stack spacing={2}>
            {fieldEntries.map(([key, value], index) => {
              const label = getLabelForKey(key);
              const displayValue = getDisplayValue(value);
              const isPositiveBool = typeof value === 'boolean' && value;
              
              return (
                <Paper 
                  key={key} 
                  elevation={1}
                  sx={{ 
                    p: 2, 
                    bgcolor: isPositiveBool ? 'success.50' : 'grey.50',
                    border: '1px solid',
                    borderColor: isPositiveBool ? 'success.200' : 'grey.200'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        minWidth: '24px',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}
                    >
                      {index + 1}
                    </Typography>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        color="primary" 
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {label}
                      </Typography>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          bgcolor: 'white',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          fontWeight: isPositiveBool ? 500 : 400,
                          color: isPositiveBool ? 'success.dark' : 'text.primary',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {displayValue}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
        
        {/* Signature verification */}
        {form.signature && (
          <Paper 
            elevation={2}
            sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'success.50', 
              border: '2px solid',
              borderColor: 'success.300'
            }}
          >
            <Typography variant="subtitle1" color="success.dark" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              Digital Signature Verification
            </Typography>
            <Typography variant="body2" color="success.dark" sx={{ mt: 1 }}>
              <strong>Signature ID:</strong> {form.signature.substring(0, 16)}...
            </Typography>
            <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
              <strong>Signed on:</strong> {new Date(form.date_signed).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This form was digitally signed and cryptographically verified for authenticity.
            </Typography>
          </Paper>
        )}
        
        {/* Form metadata */}
        <Paper 
          elevation={1}
          sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'info.50',
            border: '1px solid',
            borderColor: 'info.200'
          }}
        >
          <Typography variant="subtitle2" color="info.dark" sx={{ fontWeight: 600, mb: 1 }}>
            Form Metadata
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Form Type:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Custom Form</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Template:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{form.template_title}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Valid:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: form.is_valid ? 'success.main' : 'error.main' }}>
                {form.is_valid ? 'Yes' : 'No'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Form ID:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>{form.id}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  };

  // NEW: Render traditional consent form data without external components
  const renderTraditionalFormContent = (form) => {
    const formData = form.form_data || {};
    
    // Common field labels for traditional consent forms
    const fieldLabels = {
      // Client Information
      'client_name': 'Client Name',
      'first_name': 'First Name',
      'last_name': 'Last Name',
      'date_of_birth': 'Date of Birth',
      'email': 'Email Address',
      'phone': 'Phone Number',
      'address': 'Address',
      'city': 'City',
      'state': 'State',
      'zip_code': 'ZIP Code',
      'emergency_contact': 'Emergency Contact',
      'emergency_phone': 'Emergency Contact Phone',
      
      // Tattoo-specific
      'tattoo_description': 'Tattoo Description',
      'tattoo_location': 'Tattoo Location/Placement',
      'tattoo_size': 'Tattoo Size',
      'artist_name': 'Artist Name',
      
      // Piercing-specific
      'piercing_type': 'Piercing Type',
      'piercing_location': 'Piercing Location',
      'jewelry_type': 'Jewelry Type',
      'jewelry_material': 'Jewelry Material',
      
      // Health Questions
      'has_allergies': 'Has Allergies',
      'allergies': 'Allergies/Sensitivities',
      'has_medical_conditions': 'Has Medical Conditions',
      'medical_conditions': 'Medical Conditions',
      'taking_medications': 'Currently Taking Medications',
      'medications': 'Current Medications',
      'pregnant_nursing': 'Pregnant or Nursing',
      'heart_condition': 'Heart Condition',
      'diabetes': 'Diabetes',
      'epilepsy': 'Epilepsy',
      'hemophilia': 'Hemophilia/Bleeding Disorder',
      'skin_conditions': 'Skin Conditions',
      'hepatitis': 'Hepatitis',
      'hiv_aids': 'HIV/AIDS',
      'under_influence': 'Under Influence of Drugs/Alcohol',
      
      // Consent Acknowledgments
      'over_18': 'Is Over 18 Years Old',
      'consent_given': 'Consent Given',
      'aftercare_understood': 'Understands Aftercare Instructions',
      'risks_understood': 'Understands Risks',
      'id_verified': 'ID Verified',
      'agrees_to_terms': 'Agrees to Terms',
      
      // Signatures
      'signature': 'Client Signature',
      'date_signed': 'Date Signed',
      'witness_signature': 'Witness Signature',
      'artist_signature': 'Artist Signature',
    };
    
    const getFieldLabel = (key) => {
      if (fieldLabels[key]) {
        return fieldLabels[key];
      }
      // Convert snake_case or camelCase to Title Case
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    };
    
    const formatValue = (value) => {
      if (value === null || value === undefined) {
        return '(Not provided)';
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'string' && value.trim() === '') {
        return '(Not provided)';
      }
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '(None)';
      }
      return String(value);
    };
    
    // Filter out internal fields and signature (shown separately)
    const fieldEntries = Object.entries(formData).filter(([key]) => {
      return !key.startsWith('_') && key !== 'signature' && key !== 'date_signed';
    });
    
    // Group fields into categories
    const categories = {
      'Client Information': ['client_name', 'first_name', 'last_name', 'date_of_birth', 'email', 'phone', 'address', 'city', 'state', 'zip_code', 'emergency_contact', 'emergency_phone'],
      'Procedure Details': ['tattoo_description', 'tattoo_location', 'tattoo_size', 'artist_name', 'piercing_type', 'piercing_location', 'jewelry_type', 'jewelry_material'],
      'Health Information': ['has_allergies', 'allergies', 'has_medical_conditions', 'medical_conditions', 'taking_medications', 'medications', 'pregnant_nursing', 'heart_condition', 'diabetes', 'epilepsy', 'hemophilia', 'skin_conditions', 'hepatitis', 'hiv_aids', 'under_influence'],
      'Consent & Acknowledgments': ['over_18', 'consent_given', 'aftercare_understood', 'risks_understood', 'id_verified', 'agrees_to_terms'],
    };
    
    const categorizedFields = {};
    const uncategorizedFields = [];
    
    fieldEntries.forEach(([key, value]) => {
      let found = false;
      for (const [category, fields] of Object.entries(categories)) {
        if (fields.includes(key)) {
          if (!categorizedFields[category]) {
            categorizedFields[category] = [];
          }
          categorizedFields[category].push([key, value]);
          found = true;
          break;
        }
      }
      if (!found) {
        uncategorizedFields.push([key, value]);
      }
    });
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          Form Details
        </Typography>
        
        {Object.entries(categorizedFields).map(([category, fields]) => (
          fields.length > 0 && (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                {category}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {fields.map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {getFieldLabel(key)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatValue(value)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )
        ))}
        
        {uncategorizedFields.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {uncategorizedFields.map(([key, value]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {getFieldLabel(key)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatValue(value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {/* Show signature information */}
        {(form.signature || formData.signature) && (
          <Paper 
            elevation={2}
            sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'success.50', 
              border: '2px solid',
              borderColor: 'success.300'
            }}
          >
            <Typography variant="subtitle1" color="success.dark" sx={{ fontWeight: 600 }}>
              Digital Signature Verification
            </Typography>
            <Typography variant="body2" color="success.dark" sx={{ mt: 1 }}>
              <strong>Signed on:</strong> {new Date(form.date_signed).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This form was digitally signed and verified for authenticity.
            </Typography>
          </Paper>
        )}
      </Box>
    );
  };

  // FIXED: Updated renderConsentForm to handle all form types
  const renderConsentForm = (form) => {
    // Check if this is a custom form or traditional consent form
    const isCustomForm = form.form_type === 'custom_form' || form.template_title;
    
    // Get display title
    const getFormTitle = () => {
      if (isCustomForm) {
        return form.template_title || 'Custom Form';
      }
      
      // Format traditional form type
      const typeMap = {
        'tattoo': 'Tattoo Consent Form',
        'piercing': 'Piercing Consent Form',
        'client': 'Client Screening Form',
        'client_screening': 'Client Screening Form',
      };
      
      return typeMap[form.form_type] || `${form.form_type.charAt(0).toUpperCase() + form.form_type.slice(1)} Form`;
    };
    
    console.log('Rendering form:', {
      id: form.id,
      type: form.form_type,
      isCustom: isCustomForm,
      title: form.template_title,
      images: form.images?.length || 0
    });
    
    return (
      <Paper key={form.id} sx={{ padding: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {getFormTitle()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isCustomForm && (
              <Chip 
                label="Custom Form" 
                color="primary" 
                variant="outlined" 
                size="small" 
              />
            )}
            {form.is_valid !== undefined && (
              <Chip 
                label={form.is_valid ? 'Valid' : 'Invalid'} 
                color={form.is_valid ? 'success' : 'error'} 
                size="small" 
              />
            )}
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          <strong>Date Signed:</strong> {form.date_signed ? new Date(form.date_signed).toLocaleString() : 'N/A'}
        </Typography>

        {/* Show client info for custom forms */}
        {isCustomForm && form.client_name && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <strong>Client:</strong> {form.client_name}
          </Typography>
        )}

        {/* Show signature status */}
        {form.signature && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            Digitally Signed
          </Typography>
        )}
    
        {/* Image display */}
        {form.images && Array.isArray(form.images) && form.images.length > 0 ? (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Uploaded ID Images ({form.images.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {form.images.map((image) => (
                <Box 
                  key={image.id}
                  sx={{ 
                    width: '100%',
                    maxWidth: '600px',
                    minHeight: '300px',
                    position: 'relative',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <StandardImage 
                    imageId={image.id}
                    imageType={isCustomForm ? 'custom_form' : 'consent_form'}
                    alt="Uploaded ID"
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No images uploaded with this form.
          </Typography>
        )}
    
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          {isCustomForm ? renderCustomFormContent(form) : renderTraditionalFormContent(form)}
        </Box>
      </Paper>
    );
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ padding: 3, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Consent Forms for {clientName}
        </Typography>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleBackToProfile}
          fullWidth
          sx={{ mb: 4 }}
        >
          Back to Client Profile
        </Button>
        
        {/* Date Range Filter UI */}
        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Filter Consent Forms
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="filter-option-label">Filter By</InputLabel>
                <Select
                  labelId="filter-option-label"
                  value={filterOption}
                  onChange={handleFilterOptionChange}
                  label="Filter By"
                >
                  <MenuItem value="all">All Forms</MenuItem>
                  <MenuItem value="date_range">Date Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {filterOption === 'date_range' && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    inputProps={{
                      max: endDate || undefined
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    inputProps={{
                      min: startDate || undefined
                    }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  disabled={loading}
                >
                  Apply Filters
                </Button>
                
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  disabled={loading}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
            
            {filterError && (
              <Grid item xs={12}>
                <Alert severity="error">{filterError}</Alert>
              </Grid>
            )}
            
            {filtersApplied && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {filterOption === 'all' 
                    ? 'Showing all consent forms' 
                    : `Showing forms from ${startDate} to ${endDate}`}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Record Storage Disclaimer */}
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2, 
            mb: 2, 
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '& .MuiAlert-icon': { color: 'info.main' }
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Records are stored as provided. Users are responsible for reviewing and retrieving records as needed.
          </Typography>
        </Alert>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading consent forms...</Typography>
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 4 }}>
          {!filtersApplied ? (
            <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                Please select filter options and click "Apply Filters"
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Use "All Forms" to see all consent forms or specify a date range.
              </Typography>
            </Paper>
          ) : !loading && consentForms.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom>
                Found {consentForms.length} consent form{consentForms.length !== 1 ? 's' : ''}
              </Typography>
              {consentForms.map((form) => renderConsentForm(form))}
            </>
          ) : (
            !loading && (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No consent forms found for this client with the selected filters.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Try changing your filter options or check back later.
                </Typography>
              </Paper>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ConsentFormsPage;